import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminLayout from "../AdminLayout";
import { API_BASE } from "@/config/api";
import {
  DEFAULT_CONTACT_FORM_CONFIG,
  mergeContactFormConfig,
  type ContactFormConfig,
  type FormOption,
} from "@/lib/contactFormConfig";
import { saveTextOverrides, siteTextDefault } from "../siteTextsApi";
import type { SiteTextKey } from "@/lib/siteTexts";

const inputCls = "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm";
const labelCls = "block text-xs font-medium text-foreground-600 mb-1";
const cardCls = "rounded-lg border border-background-200 bg-background-50 p-5";
const smallBtnCls = "text-xs text-foreground-500 hover:text-foreground-700 cursor-pointer disabled:opacity-30";

// Tarjetas de contacto del costado: los títulos viven en "Textos del sitio" y los datos en "Datos del sitio".
interface ContactCards {
  titulo_whatsapp: string;
  titulo_telefono: string;
  titulo_email: string;
  phone_display: string;
  phone_raw: string;
  email: string;
}

const CARD_TEXT_KEYS: Record<"titulo_whatsapp" | "titulo_telefono" | "titulo_email", SiteTextKey> = {
  titulo_whatsapp: "contacto.card_whatsapp",
  titulo_telefono: "contacto.card_telefono",
  titulo_email: "contacto.card_email",
};

const EMPTY_CARDS: ContactCards = {
  titulo_whatsapp: siteTextDefault("contacto.card_whatsapp"),
  titulo_telefono: siteTextDefault("contacto.card_telefono"),
  titulo_email: siteTextDefault("contacto.card_email"),
  phone_display: "",
  phone_raw: "",
  email: "",
};

type CampoKey = keyof ContactFormConfig["campos"];
type TopKey = Exclude<keyof ContactFormConfig, "campos">;

function Toggle({
  checked,
  onChange,
  disabled,
  children,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  children: string;
}) {
  return (
    <label className={`inline-flex items-center gap-2 text-sm ${disabled ? "text-foreground-400" : "text-foreground-700 cursor-pointer"}`}>
      <input
        type="checkbox"
        className="w-4 h-4 accent-primary-500"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {children}
    </label>
  );
}

export default function AdminContactForm() {
  const [cfg, setCfg] = useState<ContactFormConfig>(DEFAULT_CONTACT_FORM_CONFIG);
  // Se edita como texto para que borrar y escribir no deje un 0 pegado; se convierte al guardar.
  const [maxText, setMaxText] = useState(String(DEFAULT_CONTACT_FORM_CONFIG.campos.mensaje.max_caracteres));
  const [cards, setCards] = useState<ContactCards>(EMPTY_CARDS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    fetch(`${API_BASE}/bootstrap`)
      .then((res) => res.json())
      .then((json) => {
        const loaded = mergeContactFormConfig(json.contentBlocks?.contacto?.form_config?.[0]);
        setCfg(loaded);
        setMaxText(String(loaded.campos.mensaje.max_caracteres));
        const texts: Record<string, string> = json.contentBlocks?.textos?.site?.[0]?.values ?? {};
        const pick = (k: SiteTextKey) => (typeof texts[k] === "string" ? texts[k] : siteTextDefault(k));
        setCards({
          titulo_whatsapp: pick(CARD_TEXT_KEYS.titulo_whatsapp),
          titulo_telefono: pick(CARD_TEXT_KEYS.titulo_telefono),
          titulo_email: pick(CARD_TEXT_KEYS.titulo_email),
          phone_display: json.siteSettings?.phone_display ?? "",
          phone_raw: json.siteSettings?.phone_raw ?? "",
          email: json.siteSettings?.email ?? "",
        });
      })
      .finally(() => setLoading(false));
  }, []);

  function setTop(key: TopKey, value: string) {
    setCfg((c) => ({ ...c, [key]: value }));
  }

  function setCampo<K extends CampoKey>(key: K, patch: Partial<ContactFormConfig["campos"][K]>) {
    setCfg((c) => ({ ...c, campos: { ...c.campos, [key]: { ...c.campos[key], ...patch } } }));
  }

  function setOptions(key: "tipo_unidad" | "m2", fn: (opts: FormOption[]) => FormOption[]) {
    setCfg((c) => ({
      ...c,
      campos: { ...c.campos, [key]: { ...c.campos[key], opciones: fn(c.campos[key].opciones) } },
    }));
  }

  // Si el valor guardado era igual al texto, se mantiene igual al editar; si no (opciones originales), no cambia.
  function editOptionLabel(key: "tipo_unidad" | "m2", index: number, label: string) {
    setOptions(key, (opts) =>
      opts.map((o, i) => (i === index ? { value: o.value === o.label ? label : o.value, label } : o))
    );
  }

  function moveOption(key: "tipo_unidad" | "m2", index: number, dir: -1 | 1) {
    setOptions(key, (opts) => {
      const target = index + dir;
      if (target < 0 || target >= opts.length) return opts;
      const next = [...opts];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  async function handleSave() {
    setMessage(null);
    const c = cfg.campos;
    const problems: string[] = [];
    if (!cfg.lado_titulo.trim()) problems.push("el título del lado izquierdo");
    if (!cfg.titulo.trim()) problems.push("el título del formulario");
    if (!cfg.boton_enviar.trim()) problems.push("el texto del botón");
    if (!cfg.mensaje_exito.trim()) problems.push("el mensaje de éxito");
    for (const k of ["nombre", "email", "telefono", "tipo_unidad", "m2", "fecha_inicio", "mensaje"] as CampoKey[]) {
      const visible = "visible" in c[k] ? (c[k] as { visible: boolean }).visible : true;
      if (visible && !c[k].label.trim()) problems.push(`el nombre del campo «${k.replace("_", " ")}»`);
    }
    for (const k of ["tipo_unidad", "m2"] as const) {
      if (c[k].visible && c[k].opciones.filter((o) => o.label.trim()).length === 0) {
        problems.push(`al menos una opción en «${c[k].label || k}»`);
      }
    }
    if (!cards.titulo_whatsapp.trim()) problems.push("el título de la tarjeta de WhatsApp");
    if (!cards.titulo_telefono.trim()) problems.push("el título de la tarjeta de teléfono");
    if (!cards.titulo_email.trim()) problems.push("el título de la tarjeta de email");
    if (!cards.phone_display.trim()) problems.push("el teléfono");
    const phoneDigits = cards.phone_raw.replace(/\D/g, "");
    if (phoneDigits.length < 8 || phoneDigits.length > 15) {
      problems.push("el número para WhatsApp y llamadas (solo números con código de país, entre 8 y 15 dígitos)");
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cards.email.trim())) problems.push("un email de contacto válido");
    const maxCaracteres = Math.floor(Number(maxText));
    if (c.mensaje.visible && !(maxCaracteres >= 1)) problems.push("el máximo de caracteres del mensaje (un número mayor a 0)");
    if (problems.length > 0) {
      setMessage({ type: "error", text: `Falta completar: ${problems.join(", ")}.` });
      return;
    }

    const clean: ContactFormConfig = {
      ...cfg,
      campos: {
        ...cfg.campos,
        tipo_unidad: {
          ...cfg.campos.tipo_unidad,
          opciones: cfg.campos.tipo_unidad.opciones.filter((o) => o.label.trim()),
        },
        m2: { ...cfg.campos.m2, opciones: cfg.campos.m2.opciones.filter((o) => o.label.trim()) },
        mensaje: {
          ...cfg.campos.mensaje,
          max_caracteres: maxCaracteres >= 1 ? maxCaracteres : cfg.campos.mensaje.max_caracteres,
        },
      },
    };

    setSaving(true);
    try {
      const [formRes, settingsRes, textsOk] = await Promise.all([
        fetch(`${API_BASE}/content-blocks/contacto/form_config`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id: "config", ...clean }] }),
        }),
        fetch(`${API_BASE}/site-settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            phone_display: cards.phone_display.trim(),
            phone_raw: phoneDigits,
            email: cards.email.trim(),
          }),
        }),
        saveTextOverrides({
          [CARD_TEXT_KEYS.titulo_whatsapp]: cards.titulo_whatsapp.trim(),
          [CARD_TEXT_KEYS.titulo_telefono]: cards.titulo_telefono.trim(),
          [CARD_TEXT_KEYS.titulo_email]: cards.titulo_email.trim(),
        }),
      ]);
      if (!formRes.ok || !settingsRes.ok || !textsOk) {
        setMessage({ type: "error", text: "No se pudo guardar todo. Revisa tu conexión e inténtalo de nuevo." });
        return;
      }
      setCfg(clean);
      setCards((c) => ({ ...c, phone_raw: phoneDigits }));
      setMessage({ type: "success", text: "Formulario guardado. Los cambios ya se ven en la página de contacto." });
    } catch {
      setMessage({ type: "error", text: "Error de conexión." });
    } finally {
      setSaving(false);
    }
  }

  function renderOptions(key: "tipo_unidad" | "m2") {
    const opts = cfg.campos[key].opciones;
    return (
      <div className="mt-3">
        <label className={labelCls}>Opciones de la lista</label>
        <div className="space-y-2">
          {opts.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input className={inputCls} value={o.label} onChange={(e) => editOptionLabel(key, i, e.target.value)} />
              <button type="button" className={smallBtnCls} disabled={i === 0} onClick={() => moveOption(key, i, -1)} aria-label="Subir">
                ↑
              </button>
              <button
                type="button"
                className={smallBtnCls}
                disabled={i === opts.length - 1}
                onClick={() => moveOption(key, i, 1)}
                aria-label="Bajar"
              >
                ↓
              </button>
              <button
                type="button"
                className="text-xs text-accent-600 hover:text-accent-700 cursor-pointer"
                onClick={() => setOptions(key, (list) => list.filter((_, idx) => idx !== i))}
              >
                Eliminar
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setOptions(key, (list) => [...list, { value: "", label: "" }])}
          className="mt-2 text-sm font-medium text-primary-600 hover:text-primary-700 cursor-pointer"
        >
          + Agregar opción
        </button>
      </div>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <p className="text-sm text-foreground-500">Cargando...</p>
      </AdminLayout>
    );
  }

  const c = cfg.campos;

  return (
    <AdminLayout>
      <h1 className="font-heading font-bold text-2xl text-foreground-950">Formulario de contacto</h1>
      <p className="mt-1 text-sm text-foreground-500">
        Textos, campos y opciones del formulario de la página de contacto. Los mensajes que llegan se ven en
        «Contactos». Si no cambias nada, se usan los textos originales. Los datos del servidor de correo y los avisos
        por email se configuran en{" "}
        <Link to="/admin/correo" className="text-primary-600 hover:text-primary-700 underline">
          Correo del formulario
        </Link>
        .
      </p>

      {message && (
        <div
          className={`mt-4 rounded-md p-3 text-sm border ${
            message.type === "success"
              ? "bg-primary-100 border-primary-200 text-primary-800"
              : "bg-accent-100 border-accent-200 text-accent-800"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="mt-6 space-y-5">
        <section className={cardCls}>
          <h2 className="font-heading font-semibold text-foreground-900">Texto al costado del formulario</h2>
          <div className="mt-3 space-y-3">
            <div>
              <label className={labelCls}>Etiqueta pequeña (déjala vacía para ocultarla)</label>
              <input className={inputCls} value={cfg.lado_etiqueta} onChange={(e) => setTop("lado_etiqueta", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Título</label>
              <input className={inputCls} value={cfg.lado_titulo} onChange={(e) => setTop("lado_titulo", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Descripción</label>
              <textarea
                className={inputCls}
                rows={3}
                value={cfg.lado_descripcion}
                onChange={(e) => setTop("lado_descripcion", e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className={cardCls} data-section="tarjetas">
          <h2 className="font-heading font-semibold text-foreground-900">Tarjetas de contacto (WhatsApp, teléfono y email)</h2>
          <p className="mt-1 text-xs text-foreground-500">
            Los tres recuadros que aparecen bajo el texto del costado. El teléfono y el email también se usan en el
            encabezado, el pie de página y los botones de WhatsApp del sitio, así que al cambiarlos se actualizan en
            todas partes.
          </p>
          <div className="mt-3 space-y-4">
            <div className="rounded-md border border-background-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-foreground-500">Teléfono y WhatsApp (los dos recuadros usan el mismo número)</p>
              <div>
                <label className={labelCls}>Teléfono como se muestra (ej. +56 9 1234 5678)</label>
                <input
                  className={inputCls}
                  value={cards.phone_display}
                  onChange={(e) => setCards((c) => ({ ...c, phone_display: e.target.value }))}
                />
              </div>
              <div>
                <label className={labelCls}>Número para WhatsApp y llamadas (solo números, con código de país, ej. 56912345678)</label>
                <input
                  className={inputCls}
                  inputMode="numeric"
                  value={cards.phone_raw}
                  onChange={(e) => setCards((c) => ({ ...c, phone_raw: e.target.value }))}
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Título del recuadro de WhatsApp</label>
                  <input
                    className={inputCls}
                    value={cards.titulo_whatsapp}
                    onChange={(e) => setCards((c) => ({ ...c, titulo_whatsapp: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Título del recuadro de teléfono</label>
                  <input
                    className={inputCls}
                    value={cards.titulo_telefono}
                    onChange={(e) => setCards((c) => ({ ...c, titulo_telefono: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div className="rounded-md border border-background-200 p-4 space-y-3">
              <p className="text-xs font-semibold text-foreground-500">Email</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls}>Título del recuadro de email</label>
                  <input
                    className={inputCls}
                    value={cards.titulo_email}
                    onChange={(e) => setCards((c) => ({ ...c, titulo_email: e.target.value }))}
                  />
                </div>
                <div>
                  <label className={labelCls}>Email de contacto</label>
                  <input
                    className={inputCls}
                    type="email"
                    value={cards.email}
                    onChange={(e) => setCards((c) => ({ ...c, email: e.target.value }))}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={cardCls}>
          <h2 className="font-heading font-semibold text-foreground-900">Textos del formulario</h2>
          <div className="mt-3 space-y-3">
            <div>
              <label className={labelCls}>Título</label>
              <input className={inputCls} value={cfg.titulo} onChange={(e) => setTop("titulo", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Subtítulo (déjalo vacío para ocultarlo)</label>
              <input className={inputCls} value={cfg.subtitulo} onChange={(e) => setTop("subtitulo", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Texto del botón de enviar</label>
              <input className={inputCls} value={cfg.boton_enviar} onChange={(e) => setTop("boton_enviar", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Mensaje que ve la persona después de enviar</label>
              <textarea
                className={inputCls}
                rows={2}
                value={cfg.mensaje_exito}
                onChange={(e) => setTop("mensaje_exito", e.target.value)}
              />
            </div>
            <div>
              <label className={labelCls}>Aviso bajo el botón (déjalo vacío para ocultarlo)</label>
              <textarea
                className={inputCls}
                rows={2}
                value={cfg.aviso_legal}
                onChange={(e) => setTop("aviso_legal", e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className={cardCls}>
          <h2 className="font-heading font-semibold text-foreground-900">Campos del formulario</h2>
          <p className="mt-1 text-xs text-foreground-500">
            Nombre, email y teléfono siempre se muestran y son obligatorios, porque se necesitan para poder contactar a
            la persona.
          </p>

          <div className="mt-4 space-y-5">
            {(["nombre", "email", "telefono"] as const).map((k) => (
              <div key={k} className="rounded-md border border-background-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">{k}</p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Nombre del campo</label>
                    <input className={inputCls} value={c[k].label} onChange={(e) => setCampo(k, { label: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Texto de ejemplo dentro del campo</label>
                    <input
                      className={inputCls}
                      value={c[k].placeholder}
                      onChange={(e) => setCampo(k, { placeholder: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}

            {(["tipo_unidad", "m2"] as const).map((k) => (
              <div key={k} className="rounded-md border border-background-200 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">
                  {k === "tipo_unidad" ? "Tipo de unidad (lista)" : "m² aproximados (lista)"}
                </p>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Nombre del campo</label>
                    <input className={inputCls} value={c[k].label} onChange={(e) => setCampo(k, { label: e.target.value })} />
                  </div>
                  <div>
                    <label className={labelCls}>Primera opción (texto de ayuda)</label>
                    <input
                      className={inputCls}
                      value={c[k].placeholder}
                      onChange={(e) => setCampo(k, { placeholder: e.target.value })}
                    />
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-5">
                  <Toggle checked={c[k].visible} onChange={(v) => setCampo(k, { visible: v })}>
                    Mostrar este campo
                  </Toggle>
                  <Toggle checked={c[k].required} disabled={!c[k].visible} onChange={(v) => setCampo(k, { required: v })}>
                    Obligatorio
                  </Toggle>
                </div>
                {c[k].visible && renderOptions(k)}
              </div>
            ))}

            <div className="rounded-md border border-background-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">Fecha de inicio</p>
              <div className="mt-2">
                <label className={labelCls}>Nombre del campo</label>
                <input
                  className={inputCls}
                  value={c.fecha_inicio.label}
                  onChange={(e) => setCampo("fecha_inicio", { label: e.target.value })}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-5">
                <Toggle checked={c.fecha_inicio.visible} onChange={(v) => setCampo("fecha_inicio", { visible: v })}>
                  Mostrar este campo
                </Toggle>
                <Toggle
                  checked={c.fecha_inicio.required}
                  disabled={!c.fecha_inicio.visible}
                  onChange={(v) => setCampo("fecha_inicio", { required: v })}
                >
                  Obligatorio
                </Toggle>
              </div>
            </div>

            <div className="rounded-md border border-background-200 p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-foreground-400">Mensaje</p>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Nombre del campo</label>
                  <input className={inputCls} value={c.mensaje.label} onChange={(e) => setCampo("mensaje", { label: e.target.value })} />
                </div>
                <div>
                  <label className={labelCls}>Máximo de caracteres</label>
                  <input
                    className={inputCls}
                    type="number"
                    min={1}
                    value={maxText}
                    onChange={(e) => setMaxText(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className={labelCls}>Texto de ejemplo dentro del campo</label>
                <textarea
                  className={inputCls}
                  rows={2}
                  value={c.mensaje.placeholder}
                  onChange={(e) => setCampo("mensaje", { placeholder: e.target.value })}
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-5">
                <Toggle checked={c.mensaje.visible} onChange={(v) => setCampo("mensaje", { visible: v })}>
                  Mostrar este campo
                </Toggle>
                <Toggle
                  checked={c.mensaje.required}
                  disabled={!c.mensaje.visible}
                  onChange={(v) => setCampo("mensaje", { required: v })}
                >
                  Obligatorio
                </Toggle>
              </div>
            </div>
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
          >
            {saving ? "Guardando..." : "Guardar formulario"}
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm("Se cargan los textos y opciones originales. Para aplicarlos tienes que presionar «Guardar formulario». ¿Continuar?")) {
                setCfg(DEFAULT_CONTACT_FORM_CONFIG);
                setMaxText(String(DEFAULT_CONTACT_FORM_CONFIG.campos.mensaje.max_caracteres));
                setMessage(null);
              }
            }}
            className="rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-100 cursor-pointer"
          >
            Restablecer textos originales
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
